import { z } from "zod";
import { OutputAssetSchema } from "./assets";
import { OrganizationFileEntryTypeSchema } from "./organizationFiles";

export const SEARCH_COLLECTION_ENTITY_TYPE_VALUES = [
  "KNOWLEDGEBASE_ENTRY",
  "EMAIL",
  "ORGANIZATION_FILE",
] as const;
export const SearchCollectionEntityTypeEnum = z.enum(
  SEARCH_COLLECTION_ENTITY_TYPE_VALUES,
);
export type SearchCollectionEntityType = z.infer<
  typeof SearchCollectionEntityTypeEnum
>;
export const SEARCH_COLLECTION_ENTITY_TYPE =
  SearchCollectionEntityTypeEnum.enum;

export const SEARCH_COLLECTION_PROVIDER_ID_VALUES = [
  "MORPHIK",
  "FORGEPOINT",
] as const;
export const SearchCollectionProviderIdEnum = z.enum(
  SEARCH_COLLECTION_PROVIDER_ID_VALUES,
);
export type SearchCollectionProviderId = z.infer<
  typeof SearchCollectionProviderIdEnum
>;
export const SEARCH_COLLECTION_PROVIDER_ID =
  SearchCollectionProviderIdEnum.enum;
export const DEFAULT_SEARCH_COLLECTION_PROVIDER_ID =
  SEARCH_COLLECTION_PROVIDER_ID.FORGEPOINT;

/**
 * Schema for creating a search collection at the provider layer.
 * The provider uses searchCollectionId and organizationId to construct the path.
 */
export const CreateSearchCollectionProviderParamsSchema = z.object({
  searchCollectionId: z
    .string()
    .describe("The search collection ID used for path construction"),
  organizationId: z.string().describe("The organization ID"),
});
export type CreateSearchCollectionProviderParams = z.infer<
  typeof CreateSearchCollectionProviderParamsSchema
>;

/**
 * Schema for deleting a search collection at the provider layer.
 * Contains the folder ID to delete in the external provider.
 */
export const DeleteSearchCollectionProviderParamsSchema = z.object({
  folderId: z.string().describe("The folder ID to delete in the provider"),
});
export type DeleteSearchCollectionProviderParams = z.infer<
  typeof DeleteSearchCollectionProviderParamsSchema
>;

/**
 * Schema for a search collection entity.
 * A search collection represents a grouping of indexed entries for a specific provider.
 */
export const SearchCollectionSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  providerId: SearchCollectionProviderIdEnum,
  providerExternalId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type SearchCollection = z.infer<typeof SearchCollectionSchema>;

export type EmailSearchEntryMetadata = {
  entityType: typeof SEARCH_COLLECTION_ENTITY_TYPE.EMAIL;
  userId: string;
};

export type KnowledgebaseEntrySearchEntryMetadata = {
  entityType: typeof SEARCH_COLLECTION_ENTITY_TYPE.KNOWLEDGEBASE_ENTRY;
};

export type OrganizationFileSearchEntryMetadata = {
  entityType: typeof SEARCH_COLLECTION_ENTITY_TYPE.ORGANIZATION_FILE;
};

export type SearchCollectionEntryMetadata =
  | EmailSearchEntryMetadata
  | KnowledgebaseEntrySearchEntryMetadata
  | OrganizationFileSearchEntryMetadata;

/**
 * Flexible search filters where both entityType and userId are optional.
 * Used for search queries where you may want to filter by entity type without a userId,
 * or by userId without an entity type, or both.
 * Constraint: userId can only be provided when entityType is EMAIL or unset.
 */
export const SearchCollectionSearchFiltersSchema = z
  .object({
    entityType: SearchCollectionEntityTypeEnum.optional(),
    userId: z.string().optional(),
  })
  .refine(
    (data) => !data.userId || !data.entityType || data.entityType === "EMAIL",
    { message: "userId can only be used with EMAIL entity type" },
  );
export type SearchCollectionSearchFilters = z.infer<
  typeof SearchCollectionSearchFiltersSchema
>;

export const SearchCollectionEntryMetadataSchema = z.discriminatedUnion(
  "entityType",
  [
    z.object({ entityType: z.literal("EMAIL"), userId: z.string() }),
    z.object({ entityType: z.literal("KNOWLEDGEBASE_ENTRY") }),
    z.object({ entityType: z.literal("ORGANIZATION_FILE") }),
  ],
);

/**
 * Schema for a search collection entry entity.
 * Represents an individual indexed entry within a search collection.
 * Indexing state is determined by providerExternalId:
 *   - NULL: Entry is still being indexed (INDEXING)
 *   - NOT NULL: Entry has been indexed (INDEXED)
 */
export const SearchCollectionEntrySchema = z.object({
  id: z.string(),
  searchCollectionId: z.string(),
  entityId: z.string(),
  entityType: SearchCollectionEntityTypeEnum,
  providerExternalId: z.string().nullable(),
  indexedRawAssetId: z.string().nullable(),
  indexedRawText: z.string().nullable(),
  contentHash: z.string().nullish(),
  metadata: SearchCollectionEntryMetadataSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type SearchCollectionEntry = z.infer<typeof SearchCollectionEntrySchema>;

/**
 * Search collection index payload. Asset entries may include supplemental raw
 * text that should be indexed alongside extracted asset content.
 */
export const SearchCollectionIndexDataSchema = z.union([
  z.object({
    rawText: z.string(),
    rawAsset: z.undefined().optional(),
  }),
  z.object({
    rawAsset: z.string(),
    rawText: z.string().optional(),
  }),
]);
export type SearchCollectionIndexData = z.infer<
  typeof SearchCollectionIndexDataSchema
>;

/**
 * Schema for a raw result returned directly from a search collection provider.
 * Contains the external entry ID, relevance score, and chunk content.
 * This is the initial result before mapping to local IDs and enriching with entity type.
 * For image chunks, assetId is used instead of content.
 */
export const RawSearchCollectionProviderResultSchema = z.object({
  entryExternalId: z.string(),
  score: z.number(),
  content: z.string().optional(),
  assetId: z.string().optional(),
  chunkNumber: z.number().int().optional(),
});
export type RawSearchCollectionProviderResult = z.infer<
  typeof RawSearchCollectionProviderResultSchema
>;

/**
 * Schema for a mapped search collection result.
 * Contains the local entry ID, entity type, relevance score, and chunk content.
 * This is the enriched result after mapping provider external IDs to local IDs.
 * For image chunks, assetId is used instead of content.
 */
export const SearchCollectionProviderResultSchema = z.object({
  entryId: z.string(),
  entryType: SearchCollectionEntityTypeEnum,
  score: z.number(),
  content: z.string().optional(),
  assetId: z.string().optional(),
  chunkNumber: z.number().int().optional(),
});
export type SearchCollectionProviderResult = z.infer<
  typeof SearchCollectionProviderResultSchema
>;

/**
 * Schema for search parameters when querying a search collection.
 * Either assetId or text must be provided for the search query.
 */
export const SearchCollectionParamsSchema = z
  .object({
    assetId: z.string().optional(),
    text: z.string().optional(),
    limit: z.number().int().positive().optional(),
  })
  .refine((data) => data.assetId !== undefined || data.text !== undefined, {
    message: "Either assetId or text must be provided",
  });
export type SearchCollectionParams = z.infer<
  typeof SearchCollectionParamsSchema
>;

/**
 * Zod schema for search collection provider search operations.
 * Uses a discriminated union with a 'type' field for type-safe handling.
 * The providerSearchCollectionId is used by the provider to look up its own path.
 * Use this for agent actions and API endpoints that need runtime validation.
 * Supports:
 * - "assets": Array of asset IDs (single or multiple) to search
 * - "text": Raw text search
 */
export const SearchCollectionProviderSearchParamsSchema = z.discriminatedUnion(
  "type",
  [
    z.object({
      type: z.literal("assets"),
      providerSearchCollectionId: z
        .string()
        .describe(
          "The provider's collection ID (used to resolve the collection path)",
        ),
      assetIds: z
        .array(z.string())
        .min(1)
        .describe("Array of asset IDs to search (can be single or multiple)"),
      filters: SearchCollectionSearchFiltersSchema.optional(),
      topK: z.number().int().positive().optional(),
    }),
    z.object({
      type: z.literal("text"),
      providerSearchCollectionId: z
        .string()
        .describe(
          "The provider's collection ID (used to resolve the collection path)",
        ),
      text: z.string().describe("Raw text to search"),
      filters: SearchCollectionSearchFiltersSchema.optional(),
      topK: z.number().int().positive().optional(),
    }),
  ],
);

/**
 * TypeScript discriminated union for search collection provider search operations.
 * Uses a 'type' field as the discriminator for compile-time type narrowing.
 * Use this for internal TypeScript interfaces where compile-time safety is sufficient.
 * Supports:
 * - "assets": Array of asset IDs (single or multiple) for multi-asset search
 * - "text": Raw text search
 */
export type SearchCollectionProviderSearchParams =
  | {
      type: "assets";
      providerSearchCollectionId: string;
      assetIds: string[];
      filters?: SearchCollectionSearchFilters;
      topK?: number;
    }
  | {
      type: "text";
      providerSearchCollectionId: string;
      text: string;
      filters?: SearchCollectionSearchFilters;
      topK?: number;
    };

/**
 * Zod schema for search collection provider index operations.
 * Uses a discriminated union with a 'type' field for type-safe handling.
 * The providerSearchCollectionId is used by the provider to look up its own path.
 * Use this for agent actions and API endpoints that need runtime validation.
 * Supports:
 * - "asset": Single asset ID for indexing
 * - "text": Raw text for indexing
 */
export const SearchCollectionProviderIndexEntryParamsSchema =
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("asset"),
      providerSearchCollectionId: z
        .string()
        .describe(
          "The provider's collection ID (used to resolve the collection path)",
        ),
      searchCollectionEntryId: z
        .string()
        .describe("The search collection entry ID used as vector ID"),
      assetId: z.string().describe("Single asset ID to index"),
      text: z
        .string()
        .optional()
        .describe("Supplemental text to index alongside the asset"),
      metadata: SearchCollectionEntryMetadataSchema.optional(),
    }),
    z.object({
      type: z.literal("text"),
      providerSearchCollectionId: z
        .string()
        .describe(
          "The provider's collection ID (used to resolve the collection path)",
        ),
      searchCollectionEntryId: z
        .string()
        .describe("The search collection entry ID used as vector ID"),
      text: z.string().describe("Raw text to index"),
      metadata: SearchCollectionEntryMetadataSchema.optional(),
    }),
  ]);

/**
 * TypeScript discriminated union for search collection provider index operations.
 * Uses a 'type' field as the discriminator for compile-time type narrowing.
 * Use this for internal TypeScript interfaces where compile-time safety is sufficient.
 * Supports:
 * - "asset": Single asset ID for indexing
 * - "text": Raw text for indexing
 */
export type SearchCollectionProviderIndexEntryParams =
  | {
      type: "asset";
      providerSearchCollectionId: string;
      searchCollectionEntryId: string;
      assetId: string;
      text?: string;
      metadata?: SearchCollectionEntryMetadata;
    }
  | {
      type: "text";
      providerSearchCollectionId: string;
      searchCollectionEntryId: string;
      text: string;
      metadata?: SearchCollectionEntryMetadata;
    };

/**
 * Schema for an organization file with its asset in action output format.
 * Uses ISO date strings for JSON serialization.
 */
export const OutputOrganizationFileSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  assetId: z.string().nullable(),
  type: OrganizationFileEntryTypeSchema,
  parentFolderId: z.string().nullable(),
  name: z.string(),
  path: z.string(),
  depth: z.number(),
  createdAt: z.string().describe("ISO 8601 date string"),
  updatedAt: z.string().describe("ISO 8601 date string"),
  asset: OutputAssetSchema.nullable(),
});
export type OutputOrganizationFile = z.infer<
  typeof OutputOrganizationFileSchema
>;

/**
 * Schema for a single enriched universal search result.
 * Each result carries its own hydrated chunk asset and organization file inline.
 */
export const UniversalSearchResultSchema = z.object({
  entryId: z.string().describe("The search collection entry ID"),
  entryType: SearchCollectionEntityTypeEnum.describe(
    "The type of entry (EMAIL, ORGANIZATION_FILE, KNOWLEDGEBASE_ENTRY)",
  ),
  chunkAssetId: z
    .string()
    .optional()
    .describe("The chunk asset ID (PNG snippet)"),
  chunkAsset: OutputAssetSchema.nullable()
    .optional()
    .describe("Hydrated chunk asset data"),
  organizationFile: OutputOrganizationFileSchema.nullable()
    .optional()
    .describe(
      "Hydrated organization file with nested asset (null for non-ORGANIZATION_FILE entries)",
    ),
  score: z.number().describe("Relevance score (0-1)"),
  content: z
    .string()
    .optional()
    .describe(
      "The chunk content (for text chunks). Always a snippet; a trailing [snippet trimmed] marker means the chunk is longer. Use get_asset_action / load_assets on the file's asset for the full document",
    ),
  chunkNumber: z
    .number()
    .int()
    .optional()
    .describe("The chunk number within the document"),
});
export type UniversalSearchResult = z.infer<typeof UniversalSearchResultSchema>;
